import { useEffect, useRef, useState } from "react";
import { X, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitSubscriptionLead, trackEvent, gtmPush } from "../../lib/api";

const STORAGE_KEY = "vt_popup_closed_at";
const CLOSE_DAYS = 7;

function shouldSkipPopup() {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (!t) return false;
    const ms = Date.now() - Number(t);
    return ms < CLOSE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function SubscriptionPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (shouldSkipPopup()) return;

    const openPopup = (trigger) => {
      if (triggered.current) return;
      triggered.current = true;
      setOpen(true);
      trackEvent("popup_opened", { trigger });
    };

    const t = setTimeout(() => openPopup("timer_45s"), 45000);
    const onMouseMove = (e) => {
      if (e.clientY < 10) openPopup("exit_intent");
    };
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      clearTimeout(t);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    trackEvent("popup_closed");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Ingresa un correo válido.");
      return;
    }
    setLoading(true);
    try {
      await submitSubscriptionLead({ email });
      trackEvent("popup_subscribed");
      gtmPush("newsletter_subscribe");
      setSubmitted(true);
      toast.success("¡Listo! Te escribiremos con novedades.");
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    } catch {
      toast.error("No pudimos registrar tu correo. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={close}
      data-testid="subscription-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[#1F2937] border border-accent/60 p-8 md:p-10"
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 text-white/60 hover:text-accent"
          aria-label="Cerrar"
          data-testid="popup-close"
        >
          <X className="w-6 h-6" />
        </button>

        {!submitted ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="divider-accent" />
              <span className="kicker-bright">Oferta exclusiva</span>
            </div>
            <h3 className="font-display font-black text-3xl md:text-4xl text-white tracking-tightest leading-[0.95]">
              Recibe casos de éxito y <span className="text-accent">precios referencia</span>.
            </h3>
            <p className="mt-4 text-white/70 text-sm md:text-base">
              Suscríbete y te enviamos proyectos recientes con metros cuadrados, materiales y rangos de costos.
            </p>

            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="popup-form">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="w-full bg-[#1F2937] border border-white/15 pl-10 pr-4 py-4 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                  data-testid="popup-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-accent justify-center disabled:opacity-60"
                data-testid="popup-submit"
              >
                {loading ? "Enviando…" : <>Suscribirme <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-white/40">
              Sin spam. Puedes desuscribirte cuando quieras.
            </p>
          </>
        ) : (
          <div className="text-center py-6" data-testid="popup-success">
            <h3 className="font-display font-black text-3xl text-white">¡Gracias!</h3>
            <p className="mt-3 text-white/70">Pronto recibirás nuestro primer envío con casos de éxito.</p>
            <button onClick={close} className="btn-accent mt-6">Cerrar</button>
          </div>
        )}
      </div>
    </div>
  );
}
